import { googleApiKey, googleWebClientId } from 'config/constants';
import {
  DRIVE_FILE_SCOPE,
  DriveAuthCancelled,
  DriveFile,
  getDriveAccount,
  getFileMeta,
  IMPORTABLE_MIMES,
  setDriveScope,
  signInToDrive,
  switchDriveAccount,
} from 'helpers/google-drive';

/** The user closed Google's picker after signing in (unlike a cancelled sign-in). */
export class DrivePickerClosed extends DriveAuthCancelled {
  constructor() {
    super();
    this.name = 'DrivePickerClosed';
  }
}

// Google's own Drive picker. The user picks files there, and the drive.file
// token it hands back can read only those files, so it works without the
// restricted drive.readonly review the folder browser needs.

const GAPI_SRC = 'https://apis.google.com/js/api.js';

declare global {
  interface Window {
    gapi?: any;
  }
}

let pickerLoadPromise: Promise<void> | null = null;

const loadPicker = (): Promise<void> => {
  if (window.google?.picker) return Promise.resolve();
  if (pickerLoadPromise) return pickerLoadPromise;

  pickerLoadPromise = new Promise<void>((resolve, reject) => {
    const fail = () => reject(new Error('Failed to load the Google picker'));
    const onGapi = () =>
      window.gapi.load('picker', { callback: resolve, onerror: fail });
    if (window.gapi?.load) {
      onGapi();
      return;
    }
    const script = document.createElement('script');
    script.src = GAPI_SRC;
    script.async = true;
    script.onload = onGapi;
    script.onerror = fail;
    document.head.appendChild(script);
  }).catch((err) => {
    pickerLoadPromise = null;
    throw err;
  });

  return pickerLoadPromise;
};

// The picker's app id is the Cloud project number, which leads the OAuth
// client id ("784427534543-….apps.googleusercontent.com").
const projectNumber = () => googleWebClientId.split('-')[0];

/** Signs in for drive.file. Call from a click so the popup isn't blocked. */
/** Lets the user connect another Google account for the picker. */
export const switchPickerAccount = () => {
  setDriveScope(DRIVE_FILE_SCOPE);
  return switchDriveAccount();
};

export const signInForPicker = () => {
  setDriveScope(DRIVE_FILE_SCOPE);
  return signInToDrive();
};

/**
 * Opens Google's picker and resolves with the files picked, with Drive's
 * metadata (photo capture time included). Closing it rejects with
 * DriveAuthCancelled.
 */
export const pickDriveFiles = async (): Promise<DriveFile[]> => {
  if (!googleApiKey) {
    throw new Error('VITE_APP_GOOGLE_API_KEY is not configured');
  }
  const token = await signInForPicker();
  // Google's picker doesn't say which account it browses; the title does.
  const account = await getDriveAccount().catch(() => null);
  const title = account?.email
    ? `Choose photos, videos and documents · ${account.email}`
    : 'Choose photos, videos and documents';
  await loadPicker();
  const { picker } = window.google;

  const ids = await new Promise<string[]>((resolve, reject) => {
    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMimeTypes(Array.from(IMPORTABLE_MIMES).join(','));
    new picker.PickerBuilder()
      .addView(view)
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(token)
      .setDeveloperKey(googleApiKey)
      .setAppId(projectNumber())
      .setOrigin(window.location.origin)
      .setTitle(title)
      .setCallback((data: any) => {
        const action = data[picker.Response.ACTION];
        if (action === picker.Action.PICKED) {
          resolve(
            (data[picker.Response.DOCUMENTS] || []).map(
              (doc: any) => doc[picker.Document.ID] as string,
            ),
          );
        } else if (action === picker.Action.CANCEL) {
          reject(new DrivePickerClosed());
        }
      })
      .build()
      .setVisible(true);
  });

  // The picker's own document fields lack the capture time and creation
  // date the review dates memories by, so read each file's metadata.
  const files: DriveFile[] = [];
  for (let i = 0; i < ids.length; i += 8) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await Promise.all(ids.slice(i, i + 8).map(getFileMeta));
    files.push(...batch);
  }
  return files.filter((f) => f.isImportable);
};
